/**
 * Performance monitoring wrapper for task stores.
 * Wraps any ITaskStore implementation to track operation timing.
 */

import type { ITaskStore } from './ITaskStore';
import type { TaskSnapshot, LinkSnapshot, JsonPatch, PerfMetrics } from './types';

export interface PerfMonitorOptions {
  /** Log operations that exceed this threshold (ms) */
  logThreshold?: number;
  /** Track memory usage (expensive, use sparingly) */
  trackMemory?: boolean;
  /** Callback for metrics */
  onMetric?: (metric: PerfMetrics) => void;
}

const DEFAULT_OPTIONS: PerfMonitorOptions = {
  logThreshold: 50,
  trackMemory: false,
};

/**
 * Metrics storage for comparison
 */
class MetricsCollector {
  private metrics: PerfMetrics[] = [];
  private initTime?: number;
  private lastUpdateTime?: number;
  private patchCount = 0;

  record(metric: PerfMetrics): void {
    this.metrics.push(metric);

    if (metric.operation === 'hydrate') {
      this.initTime = metric.duration;
    } else if (metric.operation.startsWith('update')) {
      this.lastUpdateTime = metric.duration;
    } else if (metric.operation === 'patch') {
      this.patchCount++;
    }
  }

  getMetrics(): { initTime?: number; lastUpdateTime?: number; patchCount: number } {
    return {
      initTime: this.initTime,
      lastUpdateTime: this.lastUpdateTime,
      patchCount: this.patchCount,
    };
  }

  getAllMetrics(): PerfMetrics[] {
    return [...this.metrics];
  }

  getSummary(): Record<string, { count: number; totalMs: number; avgMs: number }> {
    const summary: Record<string, { count: number; totalMs: number; avgMs: number }> = {};

    for (const metric of this.metrics) {
      if (!summary[metric.operation]) {
        summary[metric.operation] = { count: 0, totalMs: 0, avgMs: 0 };
      }
      summary[metric.operation].count++;
      summary[metric.operation].totalMs += metric.duration;
    }

    for (const op of Object.keys(summary)) {
      summary[op].avgMs = summary[op].totalMs / summary[op].count;
    }

    return summary;
  }

  clear(): void {
    this.metrics = [];
    this.initTime = undefined;
    this.lastUpdateTime = undefined;
    this.patchCount = 0;
  }
}

// Global metrics collector for cross-store comparison
export const globalMetrics = new MetricsCollector();

/**
 * Get current memory usage (if available)
 */
function getMemoryUsage(): number | undefined {
  if ('memory' in performance) {
    return (performance as any).memory?.usedJSHeapSize;
  }
  return undefined;
}

/**
 * Create a performance-monitored wrapper around a task store
 */
export function withPerfMonitoring(
  store: ITaskStore,
  options: PerfMonitorOptions = {}
): ITaskStore {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const storeType = store.getStoreType();

  const recordMetric = (operation: string, duration: number, taskCount?: number): void => {
    const metric: PerfMetrics = {
      operation: `${storeType}:${operation}`,
      duration,
      taskCount,
      timestamp: Date.now(),
    };

    globalMetrics.record(metric);
    opts.onMetric?.(metric);

    if (duration > (opts.logThreshold ?? 50)) {
      console.log(
        `[PERF] ${storeType}:${operation}: ${duration.toFixed(2)}ms` +
          (taskCount ? ` (${taskCount} tasks)` : '')
      );
    }
  };

  const timeOperation = <T>(operation: string, fn: () => T, taskCount?: number): T => {
    const start = performance.now();
    const result = fn();
    const duration = performance.now() - start;
    recordMetric(operation, duration, taskCount);
    return result;
  };

  const timeAsyncOperation = async <T>(
    operation: string,
    fn: () => Promise<T>,
    taskCount?: number
  ): Promise<T> => {
    const start = performance.now();
    const result = await fn();
    const duration = performance.now() - start;
    recordMetric(operation, duration, taskCount);
    return result;
  };

  // Create wrapped store
  const wrappedStore: ITaskStore = {
    // Pass-through methods (no timing needed)
    getStoreType: () => store.getStoreType(),
    getSelectedTaskId: () => store.getSelectedTaskId(),
    setSelectedTaskId: (id) => store.setSelectedTaskId(id),
    getAutoSchedulingEnabled: () => store.getAutoSchedulingEnabled(),
    setAutoSchedulingEnabled: (enabled) => store.setAutoSchedulingEnabled(enabled),
    // Session change tracking (pass-through)
    addAffectedTasks: (ids) => store.addAffectedTasks(ids),
    addEditedTasks: (ids, fields) => store.addEditedTasks(ids, fields),
    addAffectedLinks: (ids) => store.addAffectedLinks(ids),
    trackAffectedLinkChanges: (removed, added) => store.trackAffectedLinkChanges(removed, added),
    clearSessionChanges: () => store.clearSessionChanges(),
    exportSessionSnapshot: () => store.exportSessionSnapshot(),
    restoreSessionSnapshot: (snapshot) => store.restoreSessionSnapshot(snapshot),
    markPendingCreate: (taskId, payload) => store.markPendingCreate(taskId, payload),
    unmarkPendingCreate: (taskId) => store.unmarkPendingCreate(taskId),
    isPendingCreate: (taskId) => store.isPendingCreate(taskId),
    getPendingCreates: () => store.getPendingCreates(),
    markPendingDelete: (taskId) => store.markPendingDelete(taskId),
    unmarkPendingDelete: (taskId) => store.unmarkPendingDelete(taskId),
    isPendingDelete: (taskId) => store.isPendingDelete(taskId),
    getPendingDeleteIds: () => store.getPendingDeleteIds(),
    getPendingProjectCreate: () => store.getPendingProjectCreate(),
    setPendingProjectCreate: (payload) => store.setPendingProjectCreate(payload),
    getAffectedTaskIds: () => store.getAffectedTaskIds(),
    getEditedTaskIds: () => store.getEditedTaskIds(),
    getEditedTaskFields: (taskId) => store.getEditedTaskFields(taskId),
    getEditedTaskFieldsMap: () => store.getEditedTaskFieldsMap(),
    getAffectedLinkIds: () => store.getAffectedLinkIds(),
    getPendingDependencyCreates: () => store.getPendingDependencyCreates(),
    getPendingDependencyDeletes: () => store.getPendingDependencyDeletes(),
    getSessionMetadata: () => store.getSessionMetadata(),
    isLoading: () => store.isLoading(),
    getError: () => store.getError(),
    setLoading: (loading) => store.setLoading(loading),
    setError: (error) => store.setError(error),
    isBatchRecording: () => store.isBatchRecording(),

    // Timed read operations
    getTasks: () => timeOperation('getTasks', () => store.getTasks()),
    getTaskValues: () => store.getTaskValues(),
    getTask: (id) => store.getTask(id), // Don't time single lookups
    getTasksRevision: () => store.getTasksRevision(),
    getLinksRevision: () => store.getLinksRevision(),
    taskHasChildren: (taskId) => store.taskHasChildren(taskId),
    getLinks: () => store.getLinks(),
    getDhtmlxTasks: () => timeOperation('getDhtmlxTasks', () => store.getDhtmlxTasks()),
    getDhtmlxLinks: () => store.getDhtmlxLinks(),

    // Timed write operations
    updateTask: (id, changes) => {
      timeOperation('updateTask', () => store.updateTask(id, changes), 1);
    },

    batchUpdateTasks: (updates) => {
      timeOperation('batchUpdateTasks', () => store.batchUpdateTasks(updates), updates.size);
    },

    addTask: (task) => {
      timeOperation('addTask', () => store.addTask(task), 1);
    },

    addTasks: (tasks) => {
      timeOperation('addTasks', () => store.addTasks(tasks), tasks.length);
    },

    removeTask: (id) => {
      timeOperation('removeTask', () => store.removeTask(id), 1);
    },

    addLink: (link) => store.addLink(link),
    removeLink: (id) => store.removeLink(id),

    setTasks: (tasks) => {
      timeOperation('setTasks', () => store.setTasks(tasks), tasks.length);
    },

    setLinks: (links) => store.setLinks(links),

    getLocationTree: () => store.getLocationTree(),
    setLocationTree: (tree) => store.setLocationTree(tree),

    // Timed batch operations
    startBatchRecording: () => {
      timeOperation('startBatchRecording', () => store.startBatchRecording());
    },

    stopBatchRecording: () => {
      return timeOperation('stopBatchRecording', () => store.stopBatchRecording());
    },

    // Subscriptions (pass-through)
    subscribe: (listener) => store.subscribe(listener),
    onPatch: (listener) => store.onPatch(listener),

    // Timed async operations
    hydrate: async (projectId) => {
      const memBefore = opts.trackMemory ? getMemoryUsage() : undefined;
      const result = await timeAsyncOperation('hydrate', () => store.hydrate(projectId));
      const memAfter = opts.trackMemory ? getMemoryUsage() : undefined;

      if (memBefore && memAfter) {
        console.log(
          `[PERF] ${storeType}:hydrate memory: ${((memAfter - memBefore) / 1024 / 1024).toFixed(2)}MB`
        );
      }

      return result;
    },

    persist: async (projectId) => {
      await timeAsyncOperation('persist', () => store.persist(projectId));
    },

    persistTaskPatches: async (taskIds) => {
      await timeAsyncOperation('persistTaskPatches', () => store.persistTaskPatches(taskIds), taskIds.length);
    },

    // Timed computed views
    buildLocationHierarchy: () => {
      return timeOperation('buildLocationHierarchy', () => store.buildLocationHierarchy());
    },

    // Metrics access
    getMetrics: () => globalMetrics.getMetrics(),
  };

  return wrappedStore;
}

/**
 * Log a performance summary to console
 */
export function logPerfSummary(): void {
  const summary = globalMetrics.getSummary();

  console.log('\n=== Performance Summary ===');
  console.table(summary);

  const metrics = globalMetrics.getMetrics();
  console.log('\nKey Metrics:');
  console.log(`  Init time: ${metrics.initTime?.toFixed(2) ?? 'N/A'}ms`);
  console.log(`  Last update: ${metrics.lastUpdateTime?.toFixed(2) ?? 'N/A'}ms`);
  console.log(`  Patch count: ${metrics.patchCount}`);
  console.log('===========================\n');
}

/**
 * Clear all collected metrics
 */
export function clearPerfMetrics(): void {
  globalMetrics.clear();
}

/**
 * Export metrics as JSON (for comparison)
 */
export function exportMetrics(): string {
  return JSON.stringify({
    summary: globalMetrics.getSummary(),
    metrics: globalMetrics.getMetrics(),
    all: globalMetrics.getAllMetrics(),
  }, null, 2);
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).storePerf = {
    logSummary: logPerfSummary,
    clear: clearPerfMetrics,
    export: exportMetrics,
    getMetrics: () => globalMetrics.getMetrics(),
    getAllMetrics: () => globalMetrics.getAllMetrics(),
  };
}
